import {
  InputAdornment,
  InputBaseComponentProps,
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
} from '@mui/material';
import { ForwardedRef, Ref, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DataAutomationId } from '@legacy/utils/ts-utils';
import { CircularProgress } from '../CircularProgress';
import { TextField } from '../TextField';

export interface AutocompleteProps<T>
  extends Omit<
      MuiAutocompleteProps<
        T,
        boolean | undefined,
        boolean | undefined,
        boolean | undefined
      >,
      'renderInput'
    >,
    DataAutomationId {
  error?: boolean;
  inputOnChange?: (v: string) => void;
  inputProps?: InputBaseComponentProps;
  inputRef?: Ref<HTMLInputElement>;
  inputStyles?: React.CSSProperties;
  label?: string;
  placeholder?: string;
  renderInput?: MuiAutocompleteProps<
    T,
    boolean | undefined,
    boolean | undefined,
    boolean | undefined
  >['renderInput'];
}

export const AutocompleteInner = <T,>(
  {
    error = false,
    inputOnChange,
    inputProps,
    inputRef,
    inputStyles,
    label,
    placeholder,
    ...rest
  }: AutocompleteProps<T>,
  ref: Ref<HTMLDivElement>,
) => {
  const { t } = useTranslation('filter');

  return (
    <MuiAutocomplete
      clearText={t('FilterAutocompleteClearSelectedOptions')}
      closeText={t('FilterAutocompleteOptionsListClose')}
      loading={rest.loading}
      loadingText={t('FilterAutocompleteOptionsLoading')}
      noOptionsText={t('FilterAutocompleteNoOptionsAvailable')}
      openText={t('FilterAutocompleteOptionsListOpen')}
      ref={ref}
      renderInput={(params) => (
        <TextField
          ariaLabel={t('FilterAutocompleteSearchPlaceholder')}
          data-automation-id={`${rest['data-automation-id']}TextField`}
          error={error}
          inputRef={inputRef}
          label={label}
          onChange={(e) => inputOnChange?.(e.target.value)}
          placeholder={placeholder ?? t('FilterAutocompleteSearchPlaceholder')}
          {...params}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  {rest.loading && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </InputAdornment>
              ),
            },
            htmlInput: {
              ...params.inputProps,
              ...inputProps,
              style: {
                ...inputStyles,
              },
            },
          }}
        />
      )}
      {...rest}
    />
  );
};

export const Autocomplete = forwardRef(AutocompleteInner) as <T>(
  props: AutocompleteProps<T> & { ref?: ForwardedRef<HTMLDivElement> },
) => ReturnType<typeof AutocompleteInner>;
